<template>
  <el-dialog :title="dialogTitle" :visible="visible" width="700px" append-to-body :class="$style.dialog" @close="handleCancel">
    <el-form ref="form" :model="localForm" :rules="formRules" label-width="120px" :class="$style.form">
      <el-form-item :label="labels.title" prop="title">
        <el-input v-model="localForm.title" :placeholder="labels.titlePlaceholder" size="medium"></el-input>
      </el-form-item>

      <el-form-item :label="labels.type" prop="type">
        <el-radio-group v-model="localForm.type" @change="handleTypeChange">
          <el-radio v-for="item in typeOptions" :key="item.value" :label="item.value">{{ item.label }}</el-radio>
        </el-radio-group>
      </el-form-item>

      <el-form-item v-if="localForm.type === 'stay'" :label="labels.minNight" prop="minNight">
        <div :class="$style.minNightControl">
          <el-button :class="$style.stepButton" @click="decreaseMinNight">－</el-button>
          <el-input v-model="localForm.minNight" :class="$style.minNightInput" inputmode="numeric" maxlength="2" @input="handleMinNightInput"></el-input>
          <el-button :class="$style.stepButton" @click="increaseMinNight">＋</el-button>
          <span :class="$style.minNightUnit">天</span>
        </div>
        <div :class="$style.minNightHint">請輸入 1-31</div>
      </el-form-item>

      <el-form-item v-else :label="labels.dateRange" prop="validFrom">
        <el-date-picker
          v-model="localForm.dateRange"
          @change="handleDateRangeChange"
          class="date-range-picker-white"
          type="daterange"
          range-separator="至"
          :start-placeholder="labels.startDate"
          :end-placeholder="labels.endDate"
          value-format="yyyy-MM-dd"
          :editable="false"
          :picker-options="pickerOptions"
          style="width: 100%"
          size="medium"
        ></el-date-picker>
      </el-form-item>

      <el-form-item :label="labels.valueType">
        <div :class="$style.valuePanel">
          <div :class="$style.valueLine">
            <div :class="$style.valueLineLabel">計算方式</div>
            <el-radio-group v-model="localForm.valueType">
              <el-radio v-for="item in valueTypeOptions" :key="item.value" :label="item.value">{{ item.label }}</el-radio>
            </el-radio-group>
          </div>

          <div :class="$style.valueLine">
            <div :class="$style.valueLineLabel">調整方式</div>
            <el-radio-group v-model="localForm.operator">
              <el-radio v-for="item in operatorOptions" :key="item.value" :label="item.value">{{ item.label }}</el-radio>
            </el-radio-group>
          </div>

          <el-form-item prop="value" :class="$style.valueAmountItem">
            <div :class="$style.amountDisplayRow">
              <span :class="$style.amountPrefix">{{ displayOperator }}</span>
              <el-input v-model="localForm.value" :class="$style.amountInput" inputmode="decimal" @input="handleValueInput"></el-input>
              <span :class="$style.amountSuffix">{{ displayUnit }}</span>
            </div>
          </el-form-item>
        </div>
      </el-form-item>

      <el-form-item :label="labels.roomType" prop="roomTypeIds">
        <div :class="$style.roomTypePreviewBox">
          <template v-if="selectedRoomTypes.length">
            <el-tooltip v-for="room in selectedRoomTypes" :key="room.id" :content="room.label" placement="top" effect="dark" popper-class="room-type-tooltip-popper">
              <el-tag :class="$style.roomTypeTag">{{ room.label }}</el-tag>
            </el-tooltip>
          </template>
          <span v-else :class="$style.roomTypePlaceholder">{{ labels.roomTypePlaceholder }}</span>
        </div>
        <el-button round :class="$style.roomTypePickerBtn" @click="openRoomTypeDialog">＋ 選擇房型</el-button>
      </el-form-item>
    </el-form>

    <el-dialog title="選取適用房型" :visible.sync="roomTypeDialogVisible" width="780px" append-to-body :class="$style.roomTypeDialog" @close="handleRoomTypeDialogCancel">
      <div :class="$style.roomTypeSearchRow">
        <el-input v-model="roomTypeSearch" placeholder="搜尋房型" clearable :class="$style.roomTypeSearchInput">
          <BaseIcon slot="suffix" name="search" class="el-input__icon" />
        </el-input>
      </div>

      <div :class="$style.roomTypeListPanel">
        <div :class="$style.roomTypeSelectAllRow">
          <el-checkbox :value="isAllFilteredRoomTypesChecked" :indeterminate="isRoomTypeIndeterminate" @change="toggleSelectAllFilteredRoomTypes">
            全選
          </el-checkbox>
          <span :class="$style.selectedCount">{{ tempRoomTypeIds.length }}</span>
        </div>

        <el-checkbox-group v-model="tempRoomTypeIds" :class="$style.roomTypeCheckboxGroup">
          <el-row :gutter="12">
            <el-col v-for="room in filteredRoomTypeOptions" :key="room.id" :span="12">
              <el-checkbox :label="room.id" border :class="[$style.roomTypeCheckboxItem, { [$style.roomTypeCheckboxItemActive]: isTempRoomTypeSelected(room.id) }]">
                <span :class="$style.roomTypeCheckboxLabel" :title="room.label">{{ room.label }}</span>
              </el-checkbox>
            </el-col>
          </el-row>
          <div v-if="!filteredRoomTypeOptions.length" :class="$style.emptyRoomTypeText">無符合搜尋條件的房型</div>
        </el-checkbox-group>
      </div>

      <div :class="$style.roomTypeDialogFooter">
        <el-button class="is-round" @click="handleRoomTypeDialogCancel">取消</el-button>
        <el-button class="is-round" type="primary" @click="handleRoomTypeDialogConfirm">確定</el-button>
      </div>
    </el-dialog>

    <span slot="footer" :class="$style.dialogFooter">
      <el-button class="is-round" @click="handleCancel">{{ labels.cancel }}</el-button>
      <el-button class="is-round" type="primary" :loading="saving" @click="handleSubmit">{{ labels.save }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { createDefaultDialogForm, DIALOG_LABELS, DIALOG_OPERATOR_OPTIONS, DIALOG_TYPE_OPTIONS, DIALOG_VALUE_TYPE_OPTIONS } from '../modules/priceAdjustment/dialogConfig'
import { buildDialogFormData, buildSubmitPayload, clampInteger, isMinNightValid, normalizeDecimalInput } from '../modules/priceAdjustment/dialogHelpers'

export default {
  name: 'PriceAdjustmentDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    formData: {
      type: Object,
      default: () => ({}),
    },
    roomTypeOptions: {
      type: Array,
      default: () => [],
    },
    saving: {
      type: Boolean,
      default: false,
    },
    labels: {
      type: Object,
      default: () => ({ ...DIALOG_LABELS }),
    },
    typeOptions: {
      type: Array,
      default: () => [...DIALOG_TYPE_OPTIONS],
    },
  },
  data() {
    return {
      localForm: createDefaultDialogForm(),
      valueTypeOptions: DIALOG_VALUE_TYPE_OPTIONS,
      operatorOptions: DIALOG_OPERATOR_OPTIONS,
      roomTypeDialogVisible: false,
      roomTypeSearch: '',
      tempRoomTypeIds: [],
    }
  },
  computed: {
    dialogTitle() {
      return this.mode === 'edit' ? this.labels.editTitle : this.labels.createTitle
    },
    formRules() {
      const isStay = this.localForm.type === 'stay'
      const isPeriod = this.localForm.type === 'period'
      return {
        title: [{ required: true, message: '請輸入優惠標題', trigger: 'blur' }],
        minNight: isStay
          ? [
              {
                validator: (rule, value, callback) => {
                  if (!isMinNightValid(value, 1, 31)) {
                    callback(new Error('連住天數需為 1-31 正整數'))
                    return
                  }
                  callback()
                },
                trigger: ['change', 'blur'],
              },
            ]
          : [],
        validFrom: isPeriod
          ? [
              {
                validator: (rule, value, callback) => {
                  const { validFrom, validUntil } = this.localForm
                  if (!validFrom || !validUntil) {
                    callback(new Error('請選擇開始與結束日期'))
                    return
                  }
                  callback()
                },
                trigger: ['change', 'blur'],
              },
            ]
          : [],
        validUntil: isPeriod
          ? [
              {
                validator: (rule, value, callback) => {
                  const { validFrom, validUntil } = this.localForm
                  if (!validFrom || !validUntil) {
                    callback(new Error('請選擇開始與結束日期'))
                    return
                  }
                  callback()
                },
                trigger: ['change', 'blur'],
              },
            ]
          : [],
        value: [
          {
            validator: (rule, value, callback) => {
              const text = String(value ?? '').trim()
              if (!text) {
                callback(new Error('請輸入金額'))
                return
              }
              if (!/^\d+(\.\d+)?$/.test(text)) {
                callback(new Error('金額格式不正確'))
                return
              }
              callback()
            },
            trigger: ['change', 'blur'],
          },
        ],
        roomTypeIds: [{ required: true, type: 'array', min: 1, message: '請至少選擇 1 個房型', trigger: 'change' }],
      }
    },
    displayOperator() {
      return this.localForm.operator === '+' ? '＋' : '－'
    },
    displayUnit() {
      return this.localForm.valueType === '%' ? '%' : '元'
    },
    selectedRoomTypes() {
      const ids = Array.isArray(this.localForm.roomTypeIds) ? this.localForm.roomTypeIds : []
      if (!ids.length) return []

      return ids
        .map((id) => {
          const room = this.roomTypeOptions.find((item) => String(item.id) === String(id))
          return room ? { id: room.id, label: room.label } : null
        })
        .filter(Boolean)
    },
    filteredRoomTypeOptions() {
      const keyword = String(this.roomTypeSearch || '')
        .trim()
        .toLowerCase()
      if (!keyword) return this.roomTypeOptions
      return this.roomTypeOptions.filter((room) =>
        String(room.label || '')
          .toLowerCase()
          .includes(keyword)
      )
    },
    isAllFilteredRoomTypesChecked() {
      const filteredIds = this.filteredRoomTypeOptions.map((room) => room.id)
      if (!filteredIds.length) return false
      return filteredIds.every((id) => this.tempRoomTypeIds.some((selectedId) => String(selectedId) === String(id)))
    },
    pickerOptions() {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return {
        disabledDate(date) {
          return date < today
        },
      }
    },
    isRoomTypeIndeterminate() {
      const filteredIds = this.filteredRoomTypeOptions.map((room) => room.id)
      if (!filteredIds.length) return false
      const checkedCount = filteredIds.filter((id) => this.tempRoomTypeIds.some((selectedId) => String(selectedId) === String(id))).length
      return checkedCount > 0 && checkedCount < filteredIds.length
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(value) {
        if (!value) return
        this.localForm = buildDialogFormData(this.formData)
        this.$nextTick(() => {
          this.$refs.form && this.$refs.form.clearValidate()
        })
      },
    },
  },
  methods: {
    handleTypeChange(type) {
      if (type === 'stay') {
        this.localForm.dateRange = []
        this.localForm.minNight = clampInteger(this.localForm.minNight, 1, 1, 31)
      } else {
        this.localForm.minNight = ''
      }
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate(['minNight', 'validFrom', 'validUntil'])
      })
    },
    handleDateRangeChange(value) {
      const [startDate, endDate] = Array.isArray(value) ? value : []
      this.localForm.validFrom = startDate || null
      this.localForm.validUntil = endDate || null
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.validateField('validFrom')
      })
    },
    handleMinNightInput(value) {
      this.localForm.minNight = clampInteger(value, '', 1, 31)
    },
    increaseMinNight() {
      const current = Number(this.localForm.minNight) || 0
      this.localForm.minNight = String(Math.min(31, current + 1))
    },
    decreaseMinNight() {
      const current = Number(this.localForm.minNight) || 1
      this.localForm.minNight = String(Math.max(1, current - 1))
    },
    handleValueInput(value) {
      this.localForm.value = normalizeDecimalInput(value)
    },
    openRoomTypeDialog() {
      this.roomTypeDialogVisible = true
      this.roomTypeSearch = ''
      this.tempRoomTypeIds = Array.isArray(this.localForm.roomTypeIds) ? [...this.localForm.roomTypeIds] : []
    },
    handleRoomTypeDialogCancel() {
      this.roomTypeDialogVisible = false
      this.roomTypeSearch = ''
      this.tempRoomTypeIds = []
    },
    handleRoomTypeDialogConfirm() {
      this.localForm.roomTypeIds = Array.isArray(this.tempRoomTypeIds) ? [...this.tempRoomTypeIds] : []
      this.roomTypeDialogVisible = false
      this.roomTypeSearch = ''
      this.tempRoomTypeIds = []
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.validateField('roomTypeIds')
      })
    },
    toggleSelectAllFilteredRoomTypes(checked) {
      const filteredIds = this.filteredRoomTypeOptions.map((room) => room.id)
      if (!filteredIds.length) return

      if (checked) {
        const selectedMap = new Map((this.tempRoomTypeIds || []).map((id) => [String(id), id]))
        filteredIds.forEach((id) => {
          selectedMap.set(String(id), id)
        })
        this.tempRoomTypeIds = Array.from(selectedMap.values())
        return
      }

      const filteredSet = new Set(filteredIds.map((id) => String(id)))
      this.tempRoomTypeIds = (this.tempRoomTypeIds || []).filter((id) => !filteredSet.has(String(id)))
    },
    isTempRoomTypeSelected(id) {
      return (this.tempRoomTypeIds || []).some((selectedId) => String(selectedId) === String(id))
    },
    handleSubmit() {
      if (!this.$refs.form) return
      this.$refs.form.validate((valid) => {
        if (!valid) return
        this.$emit('save', buildSubmitPayload(this.localForm))
      })
    },
    handleCancel() {
      this.$emit('cancel')
      this.$emit('update:visible', false)
    },
  },
}
</script>

<style lang="scss" module scoped>
:global(.date-range-picker-white.el-date-editor.el-input__inner),
:global(.date-range-picker-white.el-date-editor--daterange.el-input__inner) {
  background-color: #ffffff;
}

:global(.date-range-picker-white .el-range-input),
:global(.date-range-picker-white .el-range-separator),
:global(.date-range-picker-white .el-range__icon) {
  background-color: #ffffff;
}

.dialog {
  ::v-deep .el-dialog {
    border-radius: 14px;
  }

  ::v-deep .el-dialog__header {
    border-bottom: 1px solid #f2f2f2;
  }
}

.form {
  ::v-deep .el-form-item {
    margin-bottom: 18px;
  }

  ::v-deep .el-input-group,
  ::v-deep .el-input-group__append,
  ::v-deep .el-input-group__prepend {
    display: inline-flex;
    align-items: stretch;
    vertical-align: middle;
  }

  ::v-deep .el-input-group__prepend,
  ::v-deep .el-input-group__append {
    padding: 0;
  }

  ::v-deep .el-input-group__prepend .el-button,
  ::v-deep .el-input-group__append .el-button {
    height: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  ::v-deep .el-input-group__prepend {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  ::v-deep .el-input-group__append {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  ::v-deep .el-input-group .el-input__inner {
    border-radius: 0;
  }

  ::v-deep .el-input__inner,
  ::v-deep .el-date-editor .el-range-input {
    text-align: center;
  }

  ::v-deep input::-webkit-outer-spin-button,
  ::v-deep input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.valuePanel {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.valueLine {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.valueLineLabel {
  width: 88px;
  color: #606266;
}

.valueAmountItem {
  margin-bottom: 0;
}

.amountDisplayRow {
  display: flex;
  align-items: center;
  gap: 10px;
}

.amountPrefix,
.amountSuffix {
  font-size: 16px;
  min-width: 18px;
  text-align: center;
  color: #303133;
}

.amountInput {
  width: 120px;
}

.minNightControl {
  width: 220px;
  display: flex;
  align-items: stretch;

  > * + * {
    margin-left: 0;
  }
}

.minNightInput {
  flex: 1 1 auto;

  ::v-deep .el-input__inner {
    height: 40px;
    line-height: 40px;
    padding: 0 10px;
    text-align: center;
    border-radius: 0;
    border-left: 0;
    border-right: 0;
    background: #fff;
  }
}

.minNightHint {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
  line-height: 1.2;
}

.minNightUnit {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  color: #606266;
  font-size: 14px;
}

.stepButton {
  width: 44px;
  min-width: 44px;
  height: 44px;
  padding: 0;
  border: 1px solid #dcdfe6;
  border-radius: 0;
  background: #fff;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.stepButton:hover,
.stepButton:focus {
  background: #f5f7fa;
  color: #303133;
}

.dialogFooter {
  display: flex;
  flex-direction: row;
  gap: 0;
  padding: 8px 0 2px;

  > * {
    flex: 1 0 0;
  }

  > * + * {
    margin-left: 20px;
  }
}

.roomTypePreviewBox {
  min-height: 44px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #fff;
  padding: 4px 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.roomTypeTag {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.roomTypePlaceholder {
  color: #c0c4cc;
}

.roomTypePickerBtn {
  margin-top: 10px;
  width: 100%;
  border-color: #f3c8a4;
  color: #d46b08;
  background: #fff7ef;
  font-weight: 600;
}

.roomTypePickerBtn:hover,
.roomTypePickerBtn:focus {
  border-color: #efb27f;
  color: #c25f05;
  background: #ffefdf;
}

.roomTypeSearchRow {
  margin-bottom: 12px;
}

.roomTypeListPanel {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 12px;
}

.roomTypeSelectAllRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #f7f8fa;
  padding: 10px 12px;
  margin-bottom: 12px;
}

.selectedCount {
  color: #ec6d26;
  font-weight: 600;
}

.roomTypeCheckboxGroup {
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
}

.roomTypeCheckboxItem {
  width: 100%;
  margin: 0 0 10px;
  border-color: #dcdfe6;
  background: #fff;
  height: auto;
  min-height: 44px;
  display: flex;
  align-items: flex-start;
}

.roomTypeCheckboxItemActive {
  border-color: #ec6d26;
  background: #fff7f2;
}

.roomTypeCheckboxLabel {
  display: block;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.5;
}

.roomTypeCheckboxItem ::v-deep .el-checkbox__input {
  align-self: flex-start;
  margin-top: 2px;
}

.roomTypeCheckboxItem ::v-deep .el-checkbox__label {
  display: block;
  width: calc(100% - 8px);
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.5;
  padding-right: 0;
}

.emptyRoomTypeText {
  color: #909399;
  text-align: center;
  padding: 16px 0;
}

.roomTypeDialogFooter {
  display: flex;
  flex-direction: row;
  gap: 0;

  > * {
    flex: 1 0 0;
  }

  > * + * {
    margin-left: 20px;
  }
}

:global(.room-type-tooltip-popper) {
  width: 360px;
  max-width: 360px;
  white-space: normal !important;
  word-break: break-word !important;
  overflow-wrap: anywhere;
}
</style>
